import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiError } from "@/lib/apiError";
import api, { apiPre } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";

// Check if user has a valid token
const hasValidToken = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000;
    return !payload.stage && !payload.purpose && Date.now() < exp;
  } catch {
    return false;
  }
};

export default function Login(){
  const navigate = useNavigate();
  const [setupError, setSetupError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(1); // 1=login, 2=totp-setup (QR), 3=totp-verify, 4=email-backup
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [hasTotp, setHasTotp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  // Redirect to dashboard if already logged in with valid token
  useEffect(() => {
    if (hasValidToken() && !new URLSearchParams(window.location.search).has("reauthenticate")) {
      navigate("/campaigns", { replace: true });
    }
  }, []);

  const onEnter = (e) => { 
    if (e.key === 'Enter') { 
      if (step === 1) submitLogin(); 
      else if (step === 2) verifySetup();
      else if (step === 3) submitTOTP(); 
      else if (step === 4) submitEmailCode();
    } 
  };

  const submitLogin = async () => {
    setLoading(true);
    try {
      const form = new URLSearchParams();
      form.append("username", email);
      form.append("password", password);
      const r = await api.post("/auth/login", form, { 
        headers: {"Content-Type": "application/x-www-form-urlencoded"}
      });
      
      localStorage.setItem("pre_token", r.data.access_token);
      
      // Check if user has TOTP set up
      if (r.data.has_totp) {
        setHasTotp(true);
        setStep(3); // Go to TOTP code entry
        toast("Enter the code from your authenticator app");
      } else {
        // User needs to set up TOTP - show QR code first
        setHasTotp(false);
        setStep(2);
        await setupTOTP();
      }
    } catch (e) { 
      toast.error(apiError(e, "Login failed - check your email and password"));
    } finally {
      setLoading(false);
    }
  };

  const setupTOTP = async () => {
    setSetupError("");
    try {
      const r = await apiPre.post("/auth/setup-totp");
      setQrCode(r.data.qr_code);
      setSecret(r.data.secret);
    } catch (e) {
      const message = apiError(e, "Failed to set up authenticator");
      setSetupError(message);
      toast.error(message);
    }
  };

  const verifySetup = async () => {
    if (!code || code.length < 6) {
      toast.error("Please enter the 6-digit code from your authenticator app");
      return;
    }
    
    setLoading(true);
    try {
      const r = await apiPre.post("/auth/verify-totp", { email, code: code.trim() });
      localStorage.setItem("token", r.data.access_token);
      localStorage.removeItem("pre_token");
      toast.success("Authenticator set up successfully! Welcome to Civipen!");
      navigate("/campaigns", { replace: true });
    } catch (e) { 
      toast.error(apiError(e, "Unable to verify authenticator"));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const submitTOTP = async () => {
    if (!code || code.length < 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    
    setLoading(true);
    try {
      const r = await apiPre.post("/auth/verify-totp", { 
        email, 
        code: code.trim(),
        remember_device: rememberDevice
      });
      localStorage.setItem("token", r.data.access_token);
      localStorage.removeItem("pre_token");
      toast.success(rememberDevice ? "Welcome back! This device will be remembered for 30 days." : "Welcome back!");
      navigate("/campaigns", { replace: true });
    } catch (e) { 
      toast.error(apiError(e, "Unable to verify authenticator"));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  // Email backup flow
  const sendEmailCode = async () => {
    setLoading(true);
    try {
      const r = await apiPre.post("/auth/send-email-code");
      if (r.data.sent) {
        setEmailSent(true);
        toast.success("Verification code sent to your email");
      }
    } catch (e) {
      toast.error(apiError(e, "Failed to send email code"));
    } finally {
      setLoading(false);
    }
  };

  const submitEmailCode = async () => {
    if (!emailCode || emailCode.length < 6) {
      toast.error("Please enter the 6-digit code from your email");
      return;
    }
    
    setLoading(true);
    try {
      const r = await apiPre.post("/auth/verify-email-code", { email, code: emailCode.trim() });
      // Email verification grants full access - user can set up TOTP later in profile
      localStorage.setItem("token", r.data.access_token);
      localStorage.removeItem("pre_token");
      
      if (r.data.has_totp === false) {
        toast.success("Welcome! You can set up an authenticator app in your Profile settings.");
      } else {
        toast.success("Welcome back!");
      }
      navigate("/campaigns", { replace: true });
    } catch (e) { 
      toast.error(apiError(e, "Unable to verify email code"));
      setEmailCode("");
    } finally {
      setLoading(false);
    }
  };

  const switchToEmailBackup = () => {
    setStep(4);
    setEmailSent(false);
    setEmailCode("");
  };

  const switchToTOTP = () => {
    setStep(hasTotp ? 3 : 2);
    setCode("");
  };

  return (
    <div className="min-h-screen flex" onKeyDown={onEnter}>
      {/* Left Panel - Art Deco Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <img 
          src="https://customer-assets.emergentagent.com/job_e82fdf60-1c85-4441-91fd-2adc5eced57b/artifacts/to92ourw_Copy%20of%20S%20Klomp%20Header%20%28Twitter%20Post%29.png"
          alt="1930s Art Deco"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A]/60 to-[#1E3A8A]/30" />
        
        {/* Logo Overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
          <div className="text-center">
            <h1 
              className="text-5xl font-bold text-white tracking-tight mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Civipen
            </h1>
            <p className="text-white/80 text-lg tracking-widest uppercase">
              by Overton Solutions
            </p>
          </div>
          
          {/* Art Deco decorative line */}
          <div className="mt-8 flex items-center gap-4">
            <div className="w-16 h-px bg-white/40" />
            <div className="w-2 h-2 rotate-45 bg-[#B45309]" />
            <div className="w-16 h-px bg-white/40" />
          </div>
          
          <p className="mt-8 text-white/70 text-center max-w-sm text-sm leading-relaxed">
            Empowering political engagement through innovative campaign tools
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo (shown only on smaller screens) */}
          <div className="lg:hidden text-center mb-8">
            <h1 
              className="text-3xl font-bold text-[#1E3A8A] tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Civipen
            </h1>
            <p className="text-gray-500 text-sm tracking-widest uppercase mt-1">
              by Overton Solutions
            </p>
          </div>

          {/* Civipen Logo Image */}
          <div className="flex justify-center mb-6">
            <img 
              src="/civipen-logo.png" 
              alt="Civipen" 
              className="h-16 w-auto"
            />
          </div>

          {/* Step indicator */}
          <div className="text-center">
            <h2 
              className="text-2xl font-semibold text-[#171717] tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {step === 1 && "Welcome Back"}
              {step === 2 && "Secure Your Account"}
              {step === 3 && "Two-Factor Verification"}
              {step === 4 && "Email Verification"}
            </h2>
            <p className="text-sm text-[#525252] mt-2">
              {step === 1 && "Sign in to your account"}
              {step === 2 && "Set up two-factor authentication"}
              {step === 3 && "Enter your authenticator code"}
              {step === 4 && "Check your email for the code"}
            </p>
          </div>

          {/* Step 1: Email & Password */}
          {step === 1 && (
            <div className="space-y-5" data-testid="login-step1">
              <div>
                <label className="block text-sm font-medium text-[#171717] mb-1.5">Email</label>
                <Input 
                  data-testid="login-email" 
                  type="email"
                  placeholder="you@example.com" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 rounded-sm border-gray-300 focus:border-[#1E3A8A] focus:ring-[#1E3A8A]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#171717] mb-1.5">Password</label>
                <Input 
                  data-testid="login-password" 
                  type="password" 
                  placeholder="Enter your password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 rounded-sm border-gray-300 focus:border-[#1E3A8A] focus:ring-[#1E3A8A]"
                />
              </div>
              <Button 
                data-testid="login-submit" 
                onClick={submitLogin} 
                disabled={!email || !password || loading}
                className="w-full h-11 rounded-sm bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white font-medium transition-colors"
              >
                {loading ? "Signing in..." : "Continue"}
            </Button>
          </div>
        )}

        {/* Step 2: TOTP Setup - Show QR Code First */}
        {step === 2 && (
          <div className="space-y-4" data-testid="login-step2-setup">
            <div className="text-center bg-blue-50 p-4 rounded-lg">
              <div className="text-3xl mb-2">📱</div>
              <p className="text-sm font-medium text-blue-900">
                Set up your authenticator app
              </p>
              <p className="text-xs text-blue-700 mt-1">
                This keeps your account secure
              </p>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600 mb-3">
                1. Open your authenticator app<br/>
                2. Scan this QR code:
              </p>
              {qrCode ? (
                <img 
                  data-testid="authenticator-qr"
                  src={qrCode} 
                  alt="TOTP QR Code" 
                  className="mx-auto border rounded-lg p-2 bg-white"
                  style={{ maxWidth: "180px" }}
                />
              ) : setupError ? (
                <div role="alert" data-testid="authenticator-setup-error" className="text-sm text-red-700">
                  {setupError}
                  <Button variant="outline" data-testid="authenticator-setup-retry" onClick={setupTOTP}>Retry setup</Button>
                </div>
              ) : (
                <div className="w-44 h-44 mx-auto bg-gray-100 rounded-lg flex items-center justify-center">
                  <span className="text-gray-400">Loading...</span>
                </div>
              )}
            </div>
            
            {secret && (
              <div className="text-center bg-gray-50 p-2 rounded">
                <p className="text-xs text-gray-500 mb-1">Can't scan? Enter manually:</p>
                <code data-testid="authenticator-manual-key" className="text-xs font-mono bg-white px-2 py-1 rounded border select-all break-all">
                  {secret}
                </code>
              </div>
            )}

            <div className="border-t pt-4">
              <p className="text-sm text-gray-600 mb-2 text-center">
                3. Enter the 6-digit code from your app:
              </p>
              <Input 
                data-testid="login-2fa-setup" 
                inputMode="numeric" autoComplete="one-time-code"
                placeholder="000000" 
                value={code} 
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-2xl tracking-widest"
                maxLength={6}
              />
            </div>
            
            <Button 
              data-testid="authenticator-setup-submit"
              onClick={verifySetup}
              disabled={code.length < 6 || loading}
              className="w-full"
            >
              {loading ? "Verifying..." : "Complete Setup"}
            </Button>
            
            <div className="text-center">
              <button 
                data-testid="authenticator-setup-email-backup"
                onClick={switchToEmailBackup}
                className="text-xs text-blue-600 hover:underline"
              >
                Don't have an authenticator app? Use email instead
              </button>
            </div>

            <div className="text-xs text-gray-500 text-center bg-gray-50 p-2 rounded">
              <p className="font-medium">Recommended apps:</p>
              <p>Google Authenticator • Authy • Microsoft Authenticator</p>
            </div>
          </div>
        )}

        {/* Step 3: TOTP Verification (existing users) */}
        {step === 3 && (
          <div className="space-y-4" data-testid="login-step3-verify">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-4xl mb-2">🔐</div>
              <p className="text-sm text-gray-600">
                Open your authenticator app and enter the 6-digit code
              </p>
            </div>
            <div>
              <Input 
                data-testid="login-2fa" 
                inputMode="numeric" autoComplete="one-time-code"
                placeholder="000000" 
                value={code} 
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-2xl tracking-widest"
                maxLength={6}
                autoFocus
              />
            </div>
            
            {/* Remember This Device */}
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input 
                type="checkbox" 
                checked={rememberDevice} 
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                data-testid="remember-device-checkbox"
              />
              <span className="text-sm text-gray-600">Remember this device for 30 days</span>
            </label>
            
            <Button 
              data-testid="login-2fa-submit" 
              onClick={submitTOTP}
              disabled={code.length < 6 || loading}
              className="w-full"
            >
              {loading ? "Verifying..." : "Verify & Sign In"}
            </Button>
            
            <div className="text-center">
              <button 
                data-testid="authenticator-email-backup"
                onClick={switchToEmailBackup}
                className="text-xs text-blue-600 hover:underline"
              >
                Can't access your authenticator? Use email backup
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Email Backup */}
        {step === 4 && (
          <div className="space-y-4" data-testid="login-step4-email">
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <div className="text-4xl mb-2">📧</div>
              <p className="text-sm text-amber-800 font-medium">
                Email Verification
              </p>
              <p className="text-xs text-amber-700 mt-1">
                We'll send a code to {email}
              </p>
            </div>
            
            {!emailSent ? (
              <Button 
                onClick={sendEmailCode}
                disabled={loading}
                className="w-full"
              >
                {loading ? "Sending..." : "Send Code to Email"}
              </Button>
            ) : (
              <>
                <div>
                  <label className="text-sm font-medium">Enter the code from your email</label>
                  <Input 
                    placeholder="000000" 
                    value={emailCode} 
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl tracking-widest mt-2"
                    maxLength={6}
                    autoFocus
                  />
                </div>
                <Button 
                  onClick={submitEmailCode}
                  disabled={emailCode.length < 6 || loading}
                  className="w-full"
                >
                  {loading ? "Verifying..." : "Verify & Sign In"}
                </Button>
                <button 
                  onClick={sendEmailCode}
                  className="text-xs text-blue-600 hover:underline w-full text-center"
                  disabled={loading}
                >
                  Resend code
                </button>
              </>
            )}
            
            <div className="text-center">
              <button 
                onClick={switchToTOTP}
                className="text-xs text-[#1E3A8A] hover:underline"
              >
                ← Back to authenticator app
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-4 border-t border-gray-100">
          <div className="flex justify-center gap-4 text-xs mb-3">
            <Link data-testid="login-terms-link" to="/terms" target="_blank" className="underline">Terms</Link>
            <Link data-testid="login-privacy-link" to="/privacy" target="_blank" className="underline">Privacy</Link>
          </div>
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} Overton Solutions. All rights reserved.
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
